import { useRef, useState, useMemo } from 'react';
import AssessmentItem from './AssessmentItem';
import BottomSheet from '../BottomSheet/BottomSheet';
import Spinner from '../Spinner/Spinner';
import { useToast } from '../Toast/ToastProvider';
import './AssessmentFlow.css';

export default function AssessmentFlow({
    template,
    answers,
    onSetAnswer,
    onSubmit,
    submitting,
    existingDate,
    onCancel,
}) {
    const [sectionIndex, setSectionIndex] = useState(0);
    const [showOverwrite, setShowOverwrite] = useState(false);
    const { showToast } = useToast();
    const topRef = useRef(null);

    const scrollToTop = () => {
        topRef.current?.scrollIntoView({ block: 'start' });
    };

    const sections = template.sections;
    const currentSection = sections[sectionIndex];
    const isLastSection = sectionIndex === sections.length - 1;

    const sectionProgress = useMemo(() => {
        return sections.map((section, i) => {
            if (i > sectionIndex) return 'upcoming';
            if (i < sectionIndex) return 'completed';
            return 'active';
        });
    }, [sections, sectionIndex]);

    const currentSectionAnswered = useMemo(() => {
        return currentSection.items.filter(
            item => item.type === 'severity_0_3' || item.type === 'yes_no'
        ).every(item => answers[item.id] !== undefined);
    }, [currentSection, answers]);

    const handleNext = () => {
        if (isLastSection) {
            if (existingDate) {
                setShowOverwrite(true);
            } else {
                handleSubmit();
            }
        } else {
            setSectionIndex(prev => prev + 1);
            scrollToTop();
        }
    };

    const handleBack = () => {
        if (sectionIndex > 0) {
            setSectionIndex(prev => prev - 1);
            scrollToTop();
        }
    };

    const handleSubmit = async () => {
        setShowOverwrite(false);
        try {
            await onSubmit();
            showToast('Assessment saved successfully', 'success');
            onCancel();
        } catch {
            showToast('Failed to save assessment', 'error');
        }
    };

    return (
        <div className="assessment-flow">
            <div ref={topRef} className="assessment-header">
                <h2 className="assessment-title">{template.display_name}</h2>
                <p className="assessment-desc">{template.description}</p>
            </div>

            <div className="assessment-progress">
                {sectionProgress.map((status, i) => (
                    <div
                        key={i}
                        className={`assessment-progress-dot ${status}`}
                        title={sections[i].display_name}
                    />
                ))}
            </div>

            <div className="assessment-section">
                <h3 className="section-title">{currentSection.display_name}</h3>
                <p className="section-count">
                    Section {sectionIndex + 1} of {sections.length}
                </p>

                <div className="section-items">
                    {currentSection.items.map(item => (
                        <AssessmentItem
                            key={item.id}
                            item={item}
                            value={answers[item.id]}
                            onChange={val => onSetAnswer(item.id, val)}
                        />
                    ))}
                </div>
            </div>

            <div className="assessment-nav">
                {sectionIndex > 0 ? (
                    <button className="btn-pill" onClick={handleBack}>
                        Back
                    </button>
                ) : (
                    <button className="btn-pill" onClick={onCancel}>
                        Cancel
                    </button>
                )}
                <button
                    className="btn-pill assessment-next-btn"
                    onClick={handleNext}
                    disabled={!currentSectionAnswered || submitting}
                >
                    {submitting ? <Spinner size={16} /> : isLastSection ? 'Submit' : 'Next'}
                </button>
            </div>

            {showOverwrite && (
                <BottomSheet onClose={() => setShowOverwrite(false)}>
                    {({ onClose }) => (
                        <div className="overwrite-warning">
                            <h3>Assessment Already Exists</h3>
                            <p>
                                You completed this assessment on{' '}
                                <strong>{new Date(existingDate).toLocaleDateString()}</strong>.
                                Overwrite with new responses?
                            </p>
                            <div className="overwrite-actions">
                                <button className="btn-pill" onClick={onClose}>
                                    Cancel
                                </button>
                                <button
                                    className="btn-pill assessment-next-btn"
                                    onClick={handleSubmit}
                                >
                                    Overwrite
                                </button>
                            </div>
                        </div>
                    )}
                </BottomSheet>
            )}
        </div>
    );
}
